import { Fragment } from "react";
import { Button, ListItemText, useMediaQuery } from "@mui/material";
import { Add } from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { useAccount } from "wagmi";

import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import type { IWaitListItem } from "@framework/types";
import { useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { useGlobalDialogFormCallbacks } from "@framework/shared";

import { WaitListClaimButton, waitlistJoinValidationSchema } from "../../../../../components/buttons";
import { MarketDialogKeys } from "../../../../../shared";

const WAIT_LIST_ITEM_URL = "/wait-list/item";

export const WaitListItem = () => {
  const { address: account } = useAccount();

  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IWaitListItem>
  >(WAIT_LIST_ITEM_URL, { enableInfiniteScroll: isMd });

  const { openDialog } = useGlobalDialogFormCallbacks<IWaitListItem, IWaitListItem>();

  const onWaitlistJoin = () => {
    openDialog(MarketDialogKeys.WAITLIST_JOIN, { account } as IWaitListItem, {
      refetch,
      initialValues: { account } as IWaitListItem,
      message: "dialogs.join",
      testId: "WaitListJoinDialogForm",
      //@ts-ignore
      filter: ({ item: _item, ...values }) => {
        return values;
      },
      url: WAIT_LIST_ITEM_URL,
      validationSchema: waitlistJoinValidationSchema,
    });
  };

  return (
    <Fragment>
      <Breadcrumbs path={["dashboard", "wait-list", "wait-list.item"]} />

      <PageHeader message="pages.wait-list.item.title">
        <Button variant="outlined" startIcon={<Add />} onClick={onWaitlistJoin} data-testid="WaitListJoinButton">
          <FormattedMessage id="form.buttons.join" />
        </Button>
      </PageHeader>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(waitlist => (
            <StyledListItem key={waitlist.id}>
              <ListItemText sx={{ width: 0.6 }}>{waitlist.account}</ListItemText>
              <ListItemText sx={{ width: { xs: 0.6, md: 0.2 } }}>{waitlist.list?.title}</ListItemText>
              <ListActions>
                <WaitListClaimButton listItem={waitlist} />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}
    </Fragment>
  );
};
